import { useMemo } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/features/auth/authStore';
import { useCartCount } from '@/features/shop/hooks';
import { useNotifications } from '@/features/notifications/hooks';
import { BellIcon, CartIcon } from './icons';
import mark from '@/assets/sagesse-mark.png';

/**
 * The desktop site chrome — a persistent top bar with the brand, the six
 * primary destinations and the account actions.
 *
 * The phone has no room for global navigation, so the native app hides it in a
 * bottom tab bar and repeats a title in every screen. A browser window has the
 * room, and a visitor expects the nav to be at the top and to stay there. Below
 * `--bp-site` this is display:none and the bottom tab bar takes over: same
 * product, the chrome each device expects.
 *
 * Written in plain DOM rather than the `@/ui` primitives on purpose. It has no
 * native counterpart to stay faithful to, and a <header>/<nav>/<a> tree is what
 * gives a browser its landmarks, its middle-click and its focus order for free.
 */

/** The six tabs, in tab-bar order. */
const NAV = [
  { key: 'home', to: '/home' },
  { key: 'match', to: '/match' },
  { key: 'shop', to: '/shop' },
  { key: 'community', to: '/community' },
  { key: 'me', to: '/me' },
  { key: 'more', to: '/more' },
] as const;

/**
 * Detail screens live outside the tab tree (a product page is not "/shop/..."),
 * so the URL alone cannot say which section a fan is in. This maps the ones
 * that clearly belong to a section, and leaves the rest unhighlighted rather
 * than guessing.
 */
const SECTION_OF: Record<string, string> = {
  '/product': '/shop',
  '/cart': '/shop',
  '/checkout': '/shop',
  '/order': '/shop',
  '/shop': '/shop',
  '/box-score': '/match',
  '/match-chat': '/match',
  '/roster': '/match',
  '/predict': '/match',
  '/prediction-leaderboard': '/match',
  '/voting': '/match',
  '/tickets': '/match',
  '/post': '/community',
  '/creator': '/community',
  '/story': '/community',
  '/compose-post': '/community',
  '/fans': '/community',
  '/exclusive-content': '/community',
  '/my-tickets': '/me',
  '/passport': '/me',
  '/wallet': '/me',
  '/membership': '/me',
  '/rewards': '/me',
  '/edit-profile': '/me',
  '/settings': '/more',
  '/privacy': '/more',
  '/sessions': '/more',
  '/family': '/more',
  '/hospitality': '/more',
  '/notifications': '/more',
};

function initialsOf(name?: string | null): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'SG';
  return parts
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

export function SiteHeader() {
  const { t } = useTranslation(['tabs', 'common']);
  const { pathname } = useLocation();
  const me = useAuthStore((s) => s.me);
  const cartCount = useCartCount();
  const { data: notifications } = useNotifications();
  const unread = notifications?.filter((n) => !n.read).length ?? 0;

  // "/product/abc" -> "/product" -> "/shop".
  const active = useMemo(() => {
    const root = '/' + pathname.split('/').filter(Boolean)[0];
    return SECTION_OF[root] ?? root;
  }, [pathname]);

  const name = me?.profile.display_name || me?.profile.username || null;

  return (
    <header className="site-header">
      <a className="site-skip" href="#main">
        {t('common:skipToContent')}
      </a>

      <div className="site-header-inner">
        <Link to="/home" className="site-brand" aria-label="Sagesse">
          <span
            className="site-brand-mark"
            style={{ maskImage: `url(${mark})`, WebkitMaskImage: `url(${mark})` }}
            aria-hidden="true"
          />
          <span className="site-brand-word">Sagesse</span>
        </Link>

        <nav className="site-nav" aria-label={t('common:primaryNav')}>
          {NAV.map((item) => (
            <NavLink
              key={item.key}
              to={item.to}
              className="site-nav-link"
              // The section map, not the URL prefix, decides what is current —
              // so a product page still marks Shop as the page you are on.
              aria-current={active === item.to ? 'page' : undefined}
            >
              {t('tabs:' + item.key)}
            </NavLink>
          ))}
        </nav>

        <div className="site-actions">
          <Link
            to="/notifications"
            className="site-icon-btn"
            aria-label={t('common:notifications')}
          >
            <BellIcon size={18} color="currentColor" />
            {unread > 0 ? <span className="site-badge">{unread > 9 ? '9+' : unread}</span> : null}
          </Link>

          <Link to="/cart" className="site-icon-btn" aria-label={t('common:cart')}>
            <CartIcon size={18} color="currentColor" />
            {cartCount > 0 ? (
              <span className="site-badge site-badge-gold">
                {cartCount > 9 ? '9+' : cartCount}
              </span>
            ) : null}
          </Link>

          <Link to="/me" className="site-account" aria-label={name ?? t('tabs:me')}>
            <span className="site-avatar" aria-hidden="true">
              {initialsOf(name)}
            </span>
            {name ? <span className="site-account-name">{name}</span> : null}
          </Link>
        </div>
      </div>
    </header>
  );
}
